/**
 * Asset Detail Panel
 *
 * Slide-out panel showing asset details with tabs:
 * - Overview: File info, metadata (all asset types)
 * - Governance: Assign personas to this specific asset (DATASETS ONLY)
 *
 * Part of Dataset-Level Governance Assignment feature (Sprint 8)
 *
 * DC-449/DC-450: Legacy Asset Migration (Sprint 9)
 * - Interviews and documents in journeys are now considered "legacy assets"
 * - These should be promoted to Organization Settings (Interviews/Documents tabs)
 * - "Promote to Organization" action moves asset to org settings and removes from journey
 *
 * NOTE: Governance tab only applies to datasets.
 * - Datasets: Full Pistoia baseline (Data Owner, Scientist, Steward)
 * - Interviews: Legacy asset - can be promoted to org settings
 * - Documents: Legacy asset - can be promoted to org settings
 *
 * @version 2.0.0
 * @date 2025-12-05
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Database,
  Mic,
  FileText,
  Landmark,
  TriangleAlert,
  CircleCheck,
  CircleAlert,
  CircleX,
  type LucideIcon,
} from 'lucide-react';
import { useJourneyId } from '../contexts/JourneyContext';
import './AssetDetailPanel.css';

// Surface (a): the dataset's full technical-score trajectory (TAS + DQ + Semantic
// + FAIR + ML, with a FAIR pillar drill-down). Route-split — ApexCharts is heavy
// and belongs behind a lazy boundary, not in the entry chunk (UX spec §7).
const TechnicalTrajectoryChart = React.lazy(
  () => import('./trajectory/TechnicalTrajectoryChart'),
);

// =============================================================================
// TYPES
// =============================================================================

interface Asset {
  asset_id: string;  // Server uses asset_id, not id
  type: 'dataset' | 'interview' | 'document';
  name: string;
  location: string;  // Server uses location, not file_path
  created_at: string;  // Server uses created_at, not uploaded_at
  health_score?: number;
  status: 'pending' | 'processing' | 'ready' | 'error';
}

interface Employee {
  employee_id: string;
  display_name: string;
  email: string;
  department?: string;
  job_title?: string;
  is_active: boolean;
}

// DC-1648: baseline personas now come from the per-org FAIR catalog
// (GET /api/personas, filtered client-side on is_baseline). This local shape
// is what the assignment modal renders; fetchBaselinePersonas maps the
// canonical Persona payload onto it.
interface GovernancePersonaOption {
  persona_id: string;
  label: string;
  persona_name: string;
  primary_role?: string;
  definition: string;
  is_baseline: boolean;
  is_critical: boolean;
}

interface Assignment {
  assignment_id: string;
  persona_id: string;
  persona_name: string;
  persona_label: string;
  employee_id: string;
  employee_name: string;
  employee_email: string;
  assigned_by: string;
  created_at: string;
  assignment_notes?: string;
}

interface AssetGovernanceSummary {
  asset_id: string;
  asset_name?: string;
  governance_status: 'complete' | 'partial' | 'missing';
  assignments: Assignment[];
  missing_baseline: {
    persona_id: string;
    persona_name: string;
    primary_role?: string;
  }[];
  stats: {
    total_assigned: number;
    baseline_assigned: number;
    baseline_total: number;
  };
}

interface AssetDetailPanelProps {
  asset: Asset;
  journeyId?: string;
  onClose: () => void;
  onAssignmentChange?: () => void;
  onAssetPromoted?: (assetId: string) => void;  // DC-449: Called after promoting legacy asset
  onAssetRemoved?: (assetId: string) => void;   // DC-449: Called after removing legacy asset
}

type TabType = 'overview' | 'governance';

// =============================================================================
// ROLE COLORS (v2.0: cream/teal tokens — see client/styles/compass-tokens.css)
// =============================================================================

const ROLE_COLORS: Record<string, string> = {
  'Data Producer': 'var(--tri-good)',
  'Data Enabler': 'var(--tri-teal)',
  'Data Consumer': 'var(--tri-terra)',
  'unknown': 'var(--tri-neutral)',
};

const STATUS_CONFIG: Record<'complete' | 'partial' | 'missing', { Icon: LucideIcon; label: string; color: string }> = {
  complete: { Icon: CircleCheck, label: 'Complete', color: 'var(--tri-good)' },
  partial: { Icon: CircleAlert, label: 'Partial', color: 'var(--tri-soft)' },
  missing: { Icon: CircleX, label: 'Missing', color: 'var(--tri-hard)' },
};

/** Lucide glyph per asset type (replaces the retired emoji icons). */
const ASSET_TYPE_ICONS: Record<Asset['type'], LucideIcon> = {
  dataset: Database,
  interview: Mic,
  document: FileText,
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const AssetDetailPanel: React.FC<AssetDetailPanelProps> = ({
  asset,
  journeyId: journeyIdProp,
  onClose,
  onAssignmentChange,
  onAssetPromoted,
  onAssetRemoved,
}) => {
  const journeyId = useJourneyId(journeyIdProp);
  // Only datasets have governance - default to governance for datasets, overview for others
  const isDataset = asset.type === 'dataset';
  // DC-449/DC-450: Detect legacy assets (non-dataset files in journeys)
  const isLegacyAsset = asset.type === 'interview' || asset.type === 'document';
  const [activeTab, setActiveTab] = useState<TabType>(isDataset ? 'governance' : 'overview');
  const [governanceSummary, setGovernanceSummary] = useState<AssetGovernanceSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Assignment modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState<{ persona_id: string; persona_name: string } | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [baselinePersonas, setBaselinePersonas] = useState<GovernancePersonaOption[]>([]);

  // DC-449: Legacy asset promotion state
  const [promoting, setPromoting] = useState(false);
  const [showPromoteConfirm, setShowPromoteConfirm] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  const fetchGovernanceSummary = useCallback(async () => {
    if (!asset) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `/api/governance/journeys/${journeyId}/assets/${asset.asset_id}/summary?name=${encodeURIComponent(asset.name)}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch governance summary');
      }

      const data = await response.json();
      if (data.success) {
        setGovernanceSummary(data);
      } else {
        throw new Error(data.error || 'Unknown error');
      }
    } catch (err: unknown) {
      console.error('[AssetDetailPanel] Fetch error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [asset, journeyId]);

  // Fetch governance summary when asset changes (DATASETS ONLY)
  useEffect(() => {
    if (asset && isDataset) {
      fetchGovernanceSummary();
      fetchEmployees();
      fetchBaselinePersonas();
    }
  }, [asset, isDataset, fetchGovernanceSummary]);

  const fetchEmployees = async () => {
    try {
      const response = await fetch('/api/employees');
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setEmployees(data.employees.filter((e: Employee) => e.is_active));
        }
      }
    } catch (err) {
      console.error('[AssetDetailPanel] Failed to fetch employees:', err);
    }
  };

  const fetchBaselinePersonas = async () => {
    try {
      // DC-1648: the legacy /api/governance/personas/baseline endpoint is
      // retired — read the per-org FAIR catalog and filter on is_baseline.
      const response = await fetch('/api/personas');
      if (response.ok) {
        const personas = (await response.json()) as Array<{
          id: string;
          name: string;
          slug: string | null;
          description: string | null;
          primary_role: string | null;
          is_baseline: boolean;
          is_critical: boolean;
        }>;
        setBaselinePersonas(
          (Array.isArray(personas) ? personas : [])
            .filter((p) => p.is_baseline)
            .map((p) => ({
              persona_id: p.id,
              label: p.slug ?? p.name,
              persona_name: p.name,
              primary_role: p.primary_role ?? undefined,
              definition: p.description ?? '',
              is_baseline: p.is_baseline,
              is_critical: p.is_critical,
            })),
        );
      }
    } catch (err) {
      console.error('[AssetDetailPanel] Failed to fetch baseline personas:', err);
    }
  };

  const handleAssignClick = (persona: { persona_id: string; persona_name: string }) => {
    setSelectedPersona(persona);
    setShowAssignModal(true);
  };

  const handleAssignSubmit = async (employeeId: string, notes: string) => {
    if (!asset || !selectedPersona) return;

    try {
      const response = await fetch('/api/governance/assignments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          persona_id: selectedPersona.persona_id,
          employee_id: employeeId,
          journey_id: journeyId,
          dataset_id: asset.asset_id,
          scope: 'dataset',
          assignment_notes: notes,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to create assignment');
      }

      // Refresh data
      await fetchGovernanceSummary();
      setShowAssignModal(false);
      setSelectedPersona(null);

      // Notify parent to refresh train visualization
      if (onAssignmentChange) {
        onAssignmentChange();
      }
    } catch (err: unknown) {
      console.error('[AssetDetailPanel] Assignment error:', err);
      alert(err instanceof Error ? err.message : 'Unknown error');
    }
  };

  const handleRemoveAssignment = async (assignmentId: string) => {
    if (!confirm('Are you sure you want to remove this assignment?')) {
      return;
    }

    try {
      const response = await fetch(`/api/governance/assignments/${assignmentId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to remove assignment');
      }

      await fetchGovernanceSummary();

      if (onAssignmentChange) {
        onAssignmentChange();
      }
    } catch (err: unknown) {
      console.error('[AssetDetailPanel] Remove assignment error:', err);
      alert(err instanceof Error ? err.message : 'Unknown error');
    }
  };

  // DC-449: Promote legacy asset to Organization Settings
  const handlePromoteToOrganization = async () => {
    if (!asset || !isLegacyAsset) return;

    setPromoting(true);
    setError(null);

    try {
      // Determine target endpoint based on asset type
      const targetEndpoint = asset.type === 'interview'
        ? '/api/organization/interviews/promote'
        : '/api/organization/documents/promote';

      const response = await fetch(targetEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          journey_id: journeyId,
          asset_id: asset.asset_id,
          file_name: asset.name,
          file_path: asset.location,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || `Failed to promote ${asset.type}`);
      }

      await response.json();

      // Close the confirmation dialog
      setShowPromoteConfirm(false);

      // Notify parent component
      if (onAssetPromoted) {
        onAssetPromoted(asset.asset_id);
      }

      // Close the panel with success message
      // DC-1645: Interviews live under People; Documents stay under Governance.
      alert(`${asset.type === 'interview' ? 'Interview' : 'Document'} promoted successfully! View it in ${asset.type === 'interview' ? 'People → Interviews' : 'Governance → Documents'}.`);
      onClose();
    } catch (err: unknown) {
      console.error('[AssetDetailPanel] Promote error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setPromoting(false);
    }
  };

  // DC-449: Remove legacy asset from journey (without promoting)
  const handleRemoveFromJourney = async () => {
    if (!asset) return;

    try {
      const response = await fetch(`/api/journey/${journeyId}/assets/${asset.asset_id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to remove asset');
      }

      setShowRemoveConfirm(false);

      if (onAssetRemoved) {
        onAssetRemoved(asset.asset_id);
      }

      onClose();
    } catch (err: unknown) {
      console.error('[AssetDetailPanel] Remove from journey error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  };

  const statusConfig = governanceSummary
    ? STATUS_CONFIG[governanceSummary.governance_status]
    : STATUS_CONFIG.missing;
  const StatusIcon = statusConfig.Icon;
  const AssetTypeIcon = ASSET_TYPE_ICONS[asset.type];

  return (
    <div className="asset-detail-overlay" onClick={onClose}>
      <div className="asset-detail-panel" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="panel-header">
          <div className="header-content">
            <div className="asset-icon-large">
              <AssetTypeIcon size={24} aria-hidden="true" />
            </div>
            <div className="header-text">
              <h2>{asset.name}</h2>
              <span className="asset-type-label">{asset.type}</span>
            </div>
          </div>
          <button className="close-button" onClick={onClose} aria-label="Close panel">×</button>
        </div>

        {/* Tabs */}
        <div className="panel-tabs">
          <button
            className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          {/* Governance tab - DATASETS ONLY */}
          {isDataset && (
            <button
              className={`tab ${activeTab === 'governance' ? 'active' : ''}`}
              onClick={() => setActiveTab('governance')}
            >
              <Landmark size={14} aria-hidden="true" /> Governance
              <span className="status-indicator" style={{ color: statusConfig.color }}>
                <StatusIcon size={14} aria-label={statusConfig.label} />
              </span>
            </button>
          )}
        </div>

        {/* Tab Content */}
        <div className="panel-content">
          {activeTab === 'overview' && (
            <div className="overview-tab">
              {/* DC-449/DC-450: Legacy Asset Banner */}
              {isLegacyAsset && (
                <div className="legacy-asset-banner">
                  <div className="legacy-banner-icon"><TriangleAlert size={20} aria-hidden="true" /></div>
                  <div className="legacy-banner-content">
                    <strong>Legacy Asset</strong>
                    <p>
                      {asset.type === 'interview'
                        ? 'Interview transcripts should be managed in Organization Settings, where they contribute to People Readiness scores.'
                        : 'Governance documents should be managed in Organization Settings, where they contribute to Governance Maturity scores.'}
                    </p>
                  </div>
                </div>
              )}

              <div className="info-grid">
                <div className="info-item">
                  <label>File Path</label>
                  <span>{asset.location}</span>
                </div>
                <div className="info-item">
                  <label>Status</label>
                  <span className={`status-badge status-${asset.status}`}>{asset.status}</span>
                </div>
                <div className="info-item">
                  <label>Uploaded</label>
                  <span>{new Date(asset.created_at).toLocaleString()}</span>
                </div>
                {asset.health_score !== undefined && (
                  <div className="info-item">
                    <label>Health Score</label>
                    <span>{asset.health_score.toFixed(1)}/5</span>
                  </div>
                )}
              </div>

              {/* Surface (a): this dataset's full technical maturity over time
                  (TAS + DQ + Semantic + FAIR + ML; FAIR pillar detail nested).
                  Datasets only — interviews/documents carry no technical score. */}
              {isDataset && journeyId && (
                <React.Suspense fallback={<div className="dtj-skeleton dtj-skeleton--sm" aria-hidden="true" />}>
                  <TechnicalTrajectoryChart journeyId={journeyId} assetId={asset.asset_id} />
                </React.Suspense>
              )}

              {/* DC-449: Legacy Asset Actions */}
              {isLegacyAsset && (
                <div className="legacy-asset-actions">
                  <h4>Migration Options</h4>
                  <p className="action-description">
                    Move this {asset.type} to Organization Settings to include it in your
                    organization-wide analysis.
                  </p>
                  <div className="action-buttons">
                    <button
                      className="btn btn-primary"
                      onClick={() => setShowPromoteConfirm(true)}
                      disabled={promoting}
                    >
                      {promoting ? 'Promoting...' : 'Promote to Organization'}
                    </button>
                    <button
                      className="btn btn-secondary btn-danger-outline"
                      onClick={() => setShowRemoveConfirm(true)}
                    >
                      Remove from Journey
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Governance tab content - DATASETS ONLY */}
          {activeTab === 'governance' && isDataset && (
            <div className="governance-tab">
              {loading ? (
                <div className="loading-state">Loading governance data...</div>
              ) : error ? (
                <div className="error-state">{error}</div>
              ) : (
                <>
                  {/* Governance Status Banner */}
                  <div className={`governance-status-banner status-${governanceSummary?.governance_status || 'missing'}`}>
                    <span className="status-icon" style={{ color: statusConfig.color }}>
                      <StatusIcon size={20} aria-hidden="true" />
                    </span>
                    <div className="status-text">
                      <strong>Governance: {statusConfig.label}</strong>
                      <span>
                        {governanceSummary?.stats.baseline_assigned || 0} of {governanceSummary?.stats.baseline_total || 3} baseline roles assigned
                      </span>
                    </div>
                  </div>

                  {/* Assigned Roles */}
                  <section className="governance-section">
                    <h3>Assigned Roles</h3>
                    {governanceSummary?.assignments.length === 0 ? (
                      <div className="empty-state">
                        No governance roles assigned to this dataset yet.
                      </div>
                    ) : (
                      <div className="assignment-list">
                        {governanceSummary?.assignments.map((assignment) => (
                          <div key={assignment.assignment_id} className="assignment-card">
                            <div className="assignment-role">
                              <span className="role-name">{assignment.persona_name}</span>
                              <span className="role-badge">Assigned</span>
                            </div>
                            <div className="assignment-person">
                              <div className="person-avatar">
                                {assignment.employee_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                              </div>
                              <div className="person-info">
                                <strong>{assignment.employee_name}</strong>
                                <span>{assignment.employee_email}</span>
                              </div>
                            </div>
                            <button
                              className="remove-btn"
                              onClick={() => handleRemoveAssignment(assignment.assignment_id)}
                              title="Remove assignment"
                              aria-label="Remove assignment"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>

                  {/* Missing Baseline Roles */}
                  {governanceSummary && governanceSummary.missing_baseline.length > 0 && (
                    <section className="governance-section">
                      <h3>Missing Baseline Roles</h3>
                      <p className="section-hint">
                        These roles are recommended for full governance compliance.
                      </p>
                      <div className="missing-roles-list">
                        {governanceSummary.missing_baseline.map((persona) => (
                          <div key={persona.persona_id} className="missing-role-card">
                            <div className="role-info">
                              <span className="role-icon"><TriangleAlert size={16} aria-hidden="true" /></span>
                              <div>
                                <strong>{persona.persona_name}</strong>
                                {persona.primary_role && (
                                  <span
                                    className="role-type-badge"
                                    style={{ backgroundColor: ROLE_COLORS[persona.primary_role] || ROLE_COLORS.unknown }}
                                  >
                                    {persona.primary_role}
                                  </span>
                                )}
                              </div>
                            </div>
                            <button
                              className="assign-btn"
                              onClick={() => handleAssignClick(persona)}
                            >
                              Assign
                            </button>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                  {/* Quick Assign for non-baseline */}
                  <section className="governance-section">
                    <button
                      className="btn btn-secondary btn-full"
                      onClick={() => {
                        // Show all personas for assignment
                        setSelectedPersona(null);
                        setShowAssignModal(true);
                      }}
                    >
                      + Assign Additional Role
                    </button>
                  </section>
                </>
              )}
            </div>
          )}
        </div>

        {/* Assignment Modal */}
        {showAssignModal && (
          <AssignmentModal
            asset={asset}
            selectedPersona={selectedPersona}
            personas={baselinePersonas}
            employees={employees}
            onClose={() => {
              setShowAssignModal(false);
              setSelectedPersona(null);
            }}
            onSubmit={handleAssignSubmit}
          />
        )}

        {/* DC-449: Promote Confirmation Dialog */}
        {showPromoteConfirm && (
          <div className="modal-overlay" onClick={() => setShowPromoteConfirm(false)}>
            <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="confirm-header">
                <h3>Promote to Organization</h3>
                <button className="close-button" onClick={() => setShowPromoteConfirm(false)} aria-label="Close dialog">×</button>
              </div>
              <div className="confirm-body">
                <p>
                  This will move <strong>"{asset.name}"</strong> to Organization Settings
                  where it will contribute to your{' '}
                  {asset.type === 'interview' ? 'People Readiness' : 'Governance Maturity'} score.
                </p>
                <p className="confirm-note">
                  The {asset.type} will be removed from this journey after promotion.
                </p>
                {error && <div className="error-message">{error}</div>}
              </div>
              <div className="confirm-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowPromoteConfirm(false)}
                  disabled={promoting}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handlePromoteToOrganization}
                  disabled={promoting}
                >
                  {promoting ? 'Promoting...' : 'Promote'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* DC-449: Remove Confirmation Dialog */}
        {showRemoveConfirm && (
          <div className="modal-overlay" onClick={() => setShowRemoveConfirm(false)}>
            <div className="confirm-dialog confirm-dialog--danger" onClick={(e) => e.stopPropagation()}>
              <div className="confirm-header">
                <h3>Remove from Journey</h3>
                <button className="close-button" onClick={() => setShowRemoveConfirm(false)} aria-label="Close dialog">×</button>
              </div>
              <div className="confirm-body">
                <p>
                  Are you sure you want to remove <strong>"{asset.name}"</strong> from this journey?
                </p>
                <p className="confirm-warning">
                  This will permanently delete the asset from this journey.
                  Consider using "Promote to Organization" instead to preserve the file.
                </p>
                {error && <div className="error-message">{error}</div>}
              </div>
              <div className="confirm-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowRemoveConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handleRemoveFromJourney}
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// ASSIGNMENT MODAL
// =============================================================================

interface AssignmentModalProps {
  asset: Asset;
  selectedPersona: { persona_id: string; persona_name: string } | null;
  personas: GovernancePersonaOption[];
  employees: Employee[];
  onClose: () => void;
  onSubmit: (employeeId: string, notes: string) => void;
}

const AssignmentModal: React.FC<AssignmentModalProps> = ({
  asset,
  selectedPersona,
  personas,
  employees,
  onClose,
  onSubmit,
}) => {
  const [personaId, setPersonaId] = useState(selectedPersona?.persona_id || '');
  const [employeeId, setEmployeeId] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const ModalAssetIcon = ASSET_TYPE_ICONS[asset.type];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!personaId || !employeeId) {
      alert('Please select both a role and a team member');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(employeeId, notes);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="assignment-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Assign Governance Role</h3>
          <button className="close-button" onClick={onClose} aria-label="Close dialog">×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label>Asset</label>
              <div className="asset-preview">
                <span className="asset-icon">
                  <ModalAssetIcon size={18} aria-hidden="true" />
                </span>
                <span>{asset.name}</span>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="persona-select">Select Role *</label>
              <select
                id="persona-select"
                value={personaId}
                onChange={(e) => setPersonaId(e.target.value)}
                required
                disabled={!!selectedPersona}
              >
                <option value="">-- Select a role --</option>
                {personas.map((p) => (
                  <option key={p.persona_id} value={p.persona_id}>
                    {p.persona_name} ({p.primary_role})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="employee-select">Select Team Member *</label>
              <select
                id="employee-select"
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                required
              >
                <option value="">-- Select a team member --</option>
                {employees.map((e) => (
                  <option key={e.employee_id} value={e.employee_id}>
                    {e.display_name} ({e.email})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="notes">Notes (Optional)</label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes about this assignment..."
                rows={3}
              />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Assigning...' : 'Assign Role'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssetDetailPanel;
